import { useCallback, useMemo, useRef, useState } from 'react';
import { LuCamera, LuImagePlus } from 'react-icons/lu';
import ScreenshotOverlayCard from './ScreenshotOverlayCard';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { useUIStore } from '@/store/UIStore.ts';
import { captureScreenImage, isScreenCaptureSupported } from '@/lib/capture-screen-image.ts';
import ScreenshotEditorDialog from '@/components/feedback/ScreenshotEditorDialog.tsx';
import { Popover, PopoverTrigger } from '@/components/ui/popover.tsx';
import ScreenshotHintPopoverContent from '@/components/feedback/ScreenshotHintPopoverContent.tsx';
import {
  markScreenshotPopoverDismissed,
  shouldShowScreenshotPopover,
} from '@/components/feedback/lib/screenshot-popover.ts';

const MAX_SIZE_MB = 5;
const MAX_SIZE_BYTES = MAX_SIZE_MB * 1024 * 1024;

interface ScreenshotSectionProps {
  mode: 'issue' | 'idea';
  screenshotBlob: Blob | null;
  setScreenshotBlob: (blob: Blob | null) => void;
}

export default function ScreenshotSection({ mode, screenshotBlob, setScreenshotBlob }: ScreenshotSectionProps) {
  const { t } = useTranslation();
  const setActiveSidePanel = useUIStore((state) => state.setShowSendFeedbackToplevelSidePanel);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [showScreenshotHint, setShowScreenshotHint] = useState(shouldShowScreenshotPopover());

  const previewUrl = useMemo(() => (screenshotBlob ? URL.createObjectURL(screenshotBlob) : null), [screenshotBlob]);

  const dismissHintPopover = useCallback(() => {
    setShowScreenshotHint(false);
    markScreenshotPopoverDismissed();
  }, []);

  const handleScreenshot = useCallback(async () => {
    dismissHintPopover();
    try {
      setActiveSidePanel(false);
      const blob = await captureScreenImage();
      if (blob) setScreenshotBlob(blob);
    } catch (error) {
      console.error('Failed to capture screenshot:', error);
      toast.error(t('feedback.captureFailed'));
    } finally {
      setActiveSidePanel(true);
    }
  }, [setScreenshotBlob, setActiveSidePanel, t, dismissHintPopover]);

  const handleAttachClick = () => {
    dismissHintPopover();
    inputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error(t('feedback.unsupportedFile'));
      return;
    }
    if (file.size > MAX_SIZE_BYTES) {
      toast.error(t('feedback.fileTooLarge', { size: MAX_SIZE_MB }));
      return;
    }
    setScreenshotBlob(file);
  };

  const handleRemove = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setScreenshotBlob(null);
  };

  const canCapture = isScreenCaptureSupported();

  return (
    <div className="flex flex-col gap-2">
      <p className="text-sm text-gray-700">
        {screenshotBlob
          ? t('feedback.attachedScreenshot') + ':'
          : mode === 'issue'
            ? t('feedback.screenshotIssueHint')
            : t('feedback.screenshotIdeaHint')}
      </p>

      {!screenshotBlob ? (
        <Popover open={showScreenshotHint}>
          <PopoverTrigger asChild>
            {canCapture ? (
              <button
                type="button"
                onMouseDown={dismissHintPopover}
                onClick={handleScreenshot}
                className="w-full inline-flex items-center justify-center gap-2 rounded-xl border border-gray-300 bg-white px-4 py-3 text-sm font-medium text-indigo-700 hover:border-indigo-500 hover:bg-muted transition-all focus:outline-none focus:ring-2 focus:ring-indigo-500 active:scale-95 active:shadow-inner"
                aria-label={t('feedback.captureScreenshot')}
              >
                <LuCamera className="w-5 h-5 text-indigo-700" />
                <span>{t('feedback.captureScreenshot')}</span>
              </button>
            ) : (
              <div className="w-full">
                <button
                  type="button"
                  onMouseDown={dismissHintPopover}
                  onClick={handleAttachClick}
                  className="w-full inline-flex items-center justify-center gap-2 rounded-xl border border-gray-300 bg-white px-4 py-3 text-sm font-medium text-indigo-700 hover:border-indigo-500 hover:bg-muted transition-all focus:outline-none focus:ring-2 focus:ring-indigo-500 active:scale-95 active:shadow-inner"
                  aria-label={t('feedback.attachScreenshot')}
                >
                  <LuImagePlus className="w-5 h-5 text-indigo-700" />
                  <span>{t('feedback.attachScreenshot')}</span>
                </button>
                <input ref={inputRef} type="file" accept="image/*" onChange={handleFileChange} className="hidden" />
              </div>
            )}
          </PopoverTrigger>
          <ScreenshotHintPopoverContent onDismiss={dismissHintPopover} />
        </Popover>
      ) : (
        <>
          <ScreenshotOverlayCard
            screenshot={previewUrl!}
            onRemove={handleRemove}
            onEdit={() => setIsEditorOpen(true)}
          />
          {previewUrl && isEditorOpen && (
            <ScreenshotEditorDialog
              open={isEditorOpen}
              screenshot={previewUrl}
              onClose={() => setIsEditorOpen(false)}
              onSave={(blob) => {
                setIsEditorOpen(false);
                setScreenshotBlob(blob);
              }}
            />
          )}
        </>
      )}
    </div>
  );
}
